import classNames from "classnames";
import { ActionIcon } from "PFComponents/action_icon";
import { Icon } from "PFComponents/icon";
import { useSearchInput } from "PFCore/helpers/use_search_input";
import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import css from "./search_input.module.scss";

const INPUT_VALUE_MAX_LENGTH = 255;

type SearchInputProps = {
  term: string;
  onTermChange: (newTerm: string) => void;
  rootClassName?: string;
  qaId?: string;
  placeholder?: string;
  debounceTime?: number;
  disabled?: boolean;
};

export const SearchInput = ({
  term,
  onTermChange,
  rootClassName,
  qaId,
  placeholder,
  debounceTime,
  disabled
}: SearchInputProps) => {
  const { t } = useTranslation("translation");
  const searchInputRef = useRef<HTMLInputElement>(null);

  const { handleSearchKeyDown, handleClear, handleDebounceChange } = useSearchInput({
    searchInputRef,
    onTermChange,
    debounceTime,
    withUrlChanges: false
  });

  useEffect(() => {
    if (!searchInputRef.current) {
      return;
    }
    if (!term) {
      searchInputRef.current.value = "";
    }
    if (searchInputRef.current.value !== term) {
      searchInputRef.current.value = term;
    }
  }, [term]);

  return (
    <div className={classNames(css.search, rootClassName)}>
      <input
        ref={searchInputRef}
        onChange={handleDebounceChange}
        onKeyDown={handleSearchKeyDown}
        placeholder={placeholder ?? t("search")}
        defaultValue={term || ""}
        data-qa-id={qaId}
        disabled={disabled}
        className={css.input}
        maxLength={INPUT_VALUE_MAX_LENGTH}
      />
      <div className={css.iconWrapper}>
        {term ? (
          <ActionIcon
            name="cross"
            size="xs"
            onClick={handleClear}
            disabled={disabled}
            title={t("clear")}
            classes={{ icon: css.icon }}
          />
        ) : (
          <Icon name="search" className={css.icon} />
        )}
      </div>
    </div>
  );
};
